import { Suspense, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Badge } from '@givebutter/primitives/badge';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import * as Popover from '@givebutter/primitives/popover';
import { useToast } from '@givebutter/primitives/toast';
import { pluralize } from '@givebutter/util';
import {
    faBan,
    faCheck,
    faEye,
    faPlus,
    faTrash,
    faWebhook,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { deleteWebhook, getWebhooks } from 'api/webhooks';
import { WebhookActivities } from 'assets/js/pages/account-settings/account-integrations/webhooks/WebhookActivities';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Input from 'components/input';
import * as Table from 'components/table';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import * as Tooltip from 'components/tooltip';
import { EmptyData } from 'components/util';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useIsPending } from 'customHooks/useIsPending';
import { isoDateFromServer } from 'helpers/dates';
import { isQueryKeyIndexMeta } from 'helpers/reactQuery';
import { queryClient } from 'queries/queryClient';
import { webhookQueryKeys } from 'queries/webhook-query-keys';
import { Webhook } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const columnHelper = createColumnHelper<Webhook>();

const Webhooks = () => {
    const toast = useToast();
    const { accountId } = useCurrentAccount();
    const { pagination, setPagination } = Table.useReactTableState();
    const [isPending, params] = useIsPending(
        {
            page: pagination.pageIndex + 1,
        },
        500,
    );

    const { data } = useSuspenseQuery({
        queryKey: webhookQueryKeys.webhooksFiltered(accountId, params),
        queryFn: () => getWebhooks(accountId, params),
    });

    const { mutate: deleteWebhookMutation, isPending: isDeletingWebhook } = useMutation({
        mutationFn: (webhookId: string) => deleteWebhook(accountId, webhookId),
        onSuccess: () => {
            toast.success('Deleted webhook successfully');
            queryClient.invalidateQueries({
                queryKey: webhookQueryKeys.webhooks(accountId),
                type: 'all',
                predicate: isQueryKeyIndexMeta,
            });
        },
        onError: (e: GivebutterAxiosError) => {
            toast.error(e.response?.data.message ?? 'Failed to delete webhook');
        },
    });

    const columns = useMemo(
        () => [
            columnHelper.accessor('name', {
                header: 'Name',
                cell: info => {
                    const webhook = info.row.original;
                    return (
                        <Tooltip.Root>
                            <Tooltip.Trigger asChild>
                                <div className="flex items-center gap-2">
                                    <div
                                        className={`h-2 w-2 rounded-full ${
                                            webhook.enabled ? 'bg-green-300' : 'bg-red-300'
                                        }`}
                                    />
                                    {webhook.name}
                                </div>
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                                <Tooltip.Content inverse>
                                    <Tooltip.Arrow inverse />
                                    <p>
                                        <span className="flex items-center gap-2">
                                            {webhook.enabled ? (
                                                <>
                                                    <FontAwesomeIcon icon={faCheck} />
                                                    Enabled
                                                </>
                                            ) : (
                                                <>
                                                    <FontAwesomeIcon icon={faBan} />
                                                    Disabled
                                                </>
                                            )}
                                        </span>
                                    </p>
                                </Tooltip.Content>
                            </Tooltip.Portal>
                        </Tooltip.Root>
                    );
                },
            }),
            columnHelper.accessor('events', {
                header: 'Listening for',
                cell: ({ getValue }) => {
                    const events = getValue();
                    return (
                        <Tooltip.Root>
                            <Tooltip.Trigger>
                                <Badge>
                                    {events.length} {pluralize(events.length, 'event')}
                                </Badge>
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                                <Tooltip.Content inverse>
                                    <Tooltip.Arrow inverse />
                                    <span>
                                        <ul>
                                            {events.map(item => (
                                                <li key={item}>{item}</li>
                                            ))}
                                        </ul>
                                    </span>
                                </Tooltip.Content>
                            </Tooltip.Portal>
                        </Tooltip.Root>
                    );
                },
            }),
            columnHelper.accessor('last_used_at', {
                header: 'Last used',
                cell: info => {
                    const webhook = info.row.original;
                    return webhook.last_used_at ? (
                        isoDateFromServer(webhook.last_used_at).toRelative()
                    ) : (
                        <EmptyData />
                    );
                },
            }),
            columnHelper.accessor('last_status_description', {
                header: 'Last status',
                cell: info => {
                    const webhook = info.row.original;
                    switch (webhook.last_status) {
                        case 'failed':
                            return <Badge color="error">{webhook.last_status_description}</Badge>;
                        case 'processing':
                            return <Badge>{webhook.last_status_description}</Badge>;
                        case 'succeeded':
                            return <Badge color="success">{webhook.last_status_description}</Badge>;
                        default:
                            return <EmptyData />;
                    }
                },
            }),
            columnHelper.accessor('signature', {
                header: 'Signing secret',
                cell: ({ getValue }) => {
                    const signature = getValue();
                    return (
                        <Popover.Root>
                            <Popover.Trigger asChild={true}>
                                <Button
                                    variant="transparent"
                                    size="sm"
                                    className="!pl-0"
                                    onClick={e => e.stopPropagation()}
                                >
                                    <FontAwesomeIcon icon={faEye} />
                                    View
                                </Button>
                            </Popover.Trigger>
                            <Popover.Portal>
                                <Popover.Content side="top" className="p-4">
                                    <Input.Root>
                                        <Input.Form value={signature} readOnly={true} />
                                        <Input.CopyButton
                                            onClick={e => e.preventDefault()}
                                            text={signature}
                                            inPortal={true}
                                        />
                                    </Input.Root>
                                    <Popover.Arrow className="fill-white" />
                                </Popover.Content>
                            </Popover.Portal>
                        </Popover.Root>
                    );
                },
            }),
            columnHelper.display({
                id: Table.Constants.Actions,
                cell: info => {
                    const webhook = info.row.original;
                    return (
                        <DropdownMenu.Root key={webhook.id}>
                            <Table.TableRowDropdownTrigger />
                            <DropdownMenu.Content>
                                <DropdownMenu.LinkItem
                                    to={`/accounts/${accountId}/settings/integrations/webhooks/${webhook.id}/edit`}
                                    onClick={e => e.stopPropagation()}
                                >
                                    Edit
                                </DropdownMenu.LinkItem>
                                <AlertDialog.Root type={'error'}>
                                    <AlertDialog.Trigger type={'reset'} asChild={true}>
                                        <DropdownMenu.Item
                                            className="text-red-500"
                                            onSelect={e => e.preventDefault()}
                                            onClick={e => e.stopPropagation()}
                                        >
                                            <FontAwesomeIcon icon={faTrash} /> Delete
                                        </DropdownMenu.Item>
                                    </AlertDialog.Trigger>
                                    <AlertDialog.Portal>
                                        <AlertDialog.Overlay />
                                        <AlertDialog.Content>
                                            <AlertDialog.Header>
                                                <AlertDialog.Icon />
                                                <AlertDialog.Close />
                                            </AlertDialog.Header>
                                            <AlertDialog.Body>
                                                <AlertDialog.Title>
                                                    Delete webhook
                                                </AlertDialog.Title>
                                                <AlertDialog.Description>
                                                    This webhook will immediately become unusable.
                                                    You will not be able to undo this action.
                                                </AlertDialog.Description>
                                            </AlertDialog.Body>
                                            <AlertDialog.Footer>
                                                <AlertDialog.Cancel
                                                    onClick={e => e.stopPropagation()}
                                                >
                                                    Close
                                                </AlertDialog.Cancel>
                                                <AlertDialog.Action
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        deleteWebhookMutation(webhook.id);
                                                    }}
                                                    disabled={isDeletingWebhook}
                                                >
                                                    Delete
                                                </AlertDialog.Action>
                                            </AlertDialog.Footer>
                                        </AlertDialog.Content>
                                    </AlertDialog.Portal>
                                </AlertDialog.Root>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    );
                },
            }),
        ],
        [deleteWebhookMutation, isDeletingWebhook, accountId],
    );

    const table = useReactTable({
        data: data?.data ?? [],
        columns,
        pageCount: data?.meta.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        state: {
            pagination,
        },
        onPaginationChange: setPagination,
        manualPagination: true,
        enableSorting: false,
        enableHiding: false,
        enableColumnPinning: false,
        meta: {
            onRowClick: row => row.toggleExpanded(),
            renderExpandedRow: row => (
                <Suspense>
                    <tr className="bg-gray-50">
                        <td colSpan={row.getVisibleCells().length}>
                            <div className="px-6 py-4">
                                <WebhookActivities
                                    accountId={accountId}
                                    webhookId={row.original.id}
                                />
                            </div>
                        </td>
                    </tr>
                </Suspense>
            ),
        },
    });

    useSyncPaginatedData(table, data);

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
                <span className="text-2xl font-medium">Webhooks</span>
                <div className="flex gap-4">
                    <Link to={`/accounts/${accountId}/settings/integrations/webhooks/new`}>
                        <Button variant="solid" size="sm">
                            <FontAwesomeIcon icon={faPlus} /> New webhook
                        </Button>
                    </Link>
                </div>
            </div>
            <Table.Root isLoading={isPending} variant={'bordered'} table={table}>
                <div className={'flex flex-col gap-4'}>
                    <Box className={'overflow-x-auto'}>
                        <Table.Content>
                            <Table.Header />
                            <Table.Body />
                        </Table.Content>
                        <Table.Empty variant={'secondary'}>
                            <Table.EmptyIcon>
                                <FontAwesomeIcon icon={faWebhook} />
                            </Table.EmptyIcon>
                            <Table.EmptyTitle>No webhooks found</Table.EmptyTitle>
                            <Table.EmptyDescription>
                                Create your first webhook to start receiving data from your account.
                            </Table.EmptyDescription>
                        </Table.Empty>
                    </Box>
                    <Table.Pagination>
                        <Table.PaginationPageNavigation />
                    </Table.Pagination>
                </div>
            </Table.Root>
        </div>
    );
};

export { Webhooks };
